<template>
    <div>
        <v-data-table
            :headers="headers"
            :items="items"
            class="medium-dense-table elevation-4 table-history"
            hide-default-header
            hide-default-footer
            disable-pagination
        >
            <template v-slot:header="{ props, on }">
                <thead class="v-data-table-header">
                    <tr>
                        <th
                            v-for="(header, index) in headers"
                            :key="header.value"
                            :style="{width: header.width === undefined ? 'auto': '{0}px'.format(header.width)}"
                            :class="header.className !== undefined ? header.className : ''"
                        >
                            <v-btn v-if="header.value === 'collapse'" icon color="neutral_n300" @click="toggleExpandAll">
                                <v-icon>
                                    {{ anyNotCollapsed ? "mdi-minus-box" : "mdi-plus-box" }}
                                </v-icon>
                            </v-btn>
                            <span v-else class="subtitle-2">{{ header.text }}</span>
                        </th>
                    </tr>
                </thead>
            </template>
            <template v-slot:item="{item}">
                <tr v-if="item.entry_type === 'main'">
                    <td class="col-icon">
                        <v-btn v-if="item.collapsable" icon color="g4" @click="toggleExpand(item.id)">
                            <v-icon>
                                {{ item.collapsed ? "mdi-menu-right" : "mdi-menu-down" }}
                            </v-icon>
                        </v-btn>
                    </td>
                    <td class="col-icon">
                        <router-link
                            :to="{ name: 'client_profile', params: { id: project.client_id }}"
                            style="text-decoration: none"
                        >
                            <ClientAvatar
                                :color="project.client_id_disp.color"
                                :initials="project.client_id_disp.client_initials"
                                :size="32"
                                clickable
                            ></ClientAvatar>
                        </router-link>
                    </td>
                    <td>{{ item.created_at | format_datetime }}</td>
                    <td>{{ item.creator_name }}</td>
                    <td>{{ formatAction(item) }}</td>
                </tr>
                <tr v-else-if="!item.collapsed" class="child">
                    <td></td>
                    <td colspan="4" class="field-changes">
                        <ul>
                            <li v-for="(prop, idx) in item.diff" :key="idx">
                                <span>{{prop.field}}:</span>
                                <span v-if="prop.old != null">{{formatDataValue(prop.old, prop.type)}}</span>
                                <span v-if="prop.old != null" class="arrow">&rarr;</span>
                                <span class="new">{{formatDataValue(prop.new, prop.type)}}</span>
                            </li>
                        </ul>
                    </td>
                </tr>
            </template>
        </v-data-table>
    </div>
</template>

<script>
import {computed, ref} from "@vue/composition-api";
import {$t} from "../../i18n";
import ProjectModule from "../../store/modules/project";
import AppModule from "../../store/modules/app";
import ClientAvatar from "../../components/ClientAvatar";
import {formatDate, formatNumber} from "../../plugins/formatting";

export default {
    name: "ProjectActivityView",
    components: {
        ClientAvatar
    },
    props: {
        project: {
            type: Object,
            required: true
        }
    },
    setup(props) {
        const { showError } = AppModule();
        const { fetchProjectHistory, isEventTypeCompany } = ProjectModule();

        const items = ref([]);
        const workflows = ref([]);
        const users = ref([]);
        const suppliers = ref([]);
        const topics = ref([]);
        const clients = ref([]);
        const headers = ref([
            {value: "collapse", text: "", width: 48, sortable: false, className: 'col-icon' },
            {value: "client", text: "", width: 48, sortable: false, className: 'col-icon' },
            {value: "created_at", text: $t("Date"), sortable: false },
            {value: "creator_name", text: $t("Member"), sortable: false },
            {value: "type", text: $t("Action"), sortable: false },
        ]);

        const anyNotCollapsed = computed(() => {
            for (let item of items.value) {
                if (item.collapsable && !item.collapsed)
                    return true;
            }
            return false;
        });

        const loadData = () => {
            fetchProjectHistory(props.project.id)
                .then(resp => {
                    workflows.value = resp.workflows;
                    users.value = resp.users;
                    suppliers.value = resp.suppliers;
                    topics.value = resp.topics;
                    clients.value = resp.clients;
                    items.value = makeExpandable(resp.items);
                })
                .catch(ex => showError(ex));
        }
        const makeExpandable = items => {
            let ret = [];
            let collapsable_types = ['task_changed', 'project_changed', 'project_status_changed']

            for (let item of items) {
                let collapsable = collapsable_types.indexOf(item.type) >= 0 && item.data && item.data.diff;
                ret.push({...item, collapsable, collapsed: true, entry_type: 'main'});
                if (collapsable) {
                    ret.push({diff: item.data.diff, collapsed: true, entry_type: 'child'});
                }
            }

            return ret;
        }
        const toggleExpandAll = () => {
            let collapsed = anyNotCollapsed.value;
            for (let i = 0; i < items.value.length; i++) {
                if (items.value[i].collapsable) {
                    items.value[i].collapsed = collapsed;
                    items.value[i + 1].collapsed = collapsed;
                }
            }
        }
        const toggleExpand = item_id => {
            for (let i = 0; i < items.value.length; i++) {
                if (items.value[i].id === item_id) {
                    items.value[i].collapsed = !items.value[i].collapsed;
                    items.value[i + 1].collapsed = items.value[i].collapsed;
                    break;
                }
            }
        }
        const formatWfAction = (action_text, item) => {
            if (item.data && item.data.workflow_id) {
                let wf = workflows.value.find(x => x.id === item.data.workflow_id);
                if (wf != null)
                    return '{1}: {0}'.format(wf.name, action_text);
            }
            return action_text;
        }
        const formatTaskAction = (action_text, item) => {
            // for fixing old activity data
            if (item.data && item.data.client_id && item.data.client_id.title) {
                return '{1}: {0}'.format(item.data.client_id.title, action_text);
            }
            if (item.data && item.data.title) {
                return '{1}: {0}'.format(item.data.title, action_text);
            }
            return action_text;
        }
        const formatAction = (item) => {
            const isEvent = isEventTypeCompany(props.project.account_id)

            switch (item.type) {
                case "project_created":
                    return isEvent ? $t("Event created") : $t('Project created');
                case "project_deleted":
                    return isEvent ? $t("Event deleted") : $t('Project deleted');
                case "project_workflow_assigned":
                    return formatWfAction($t('Workflow added'), item);
                case "project_workflow_removed":
                    return formatWfAction($t('Workflow removed'), item);
                case "project_workflow_updated":
                    return formatWfAction($t('Workflow updated'), item);
                case "project_cancelled":
                    return isEvent ? $t("Event cancelled") : $t('Project cancelled');
                case "project_activated":
                    return isEvent ? $t("Event reactivated") : $t('Project reactivated');
                case "project_archived":
                    return isEvent ? $t("Event archived") : $t('Project archived');
                case "project_unarchived":
                    return isEvent ? $t("Event unarchived") : $t('Project unarchived');
                case "project_changed":
                    return isEvent ? $t("Event changed") : $t('Project changed');
                case "project_status_changed":
                    if (item.data && item.data.diff) {
                        let status_change = item.data.diff.find(x => x.type === "project_status");
                        if (status_change) {
                            return isEvent ? $t("Event status changed: {0}").format(status_change.new) : $t("Project status changed: {0}").format(status_change.new);
                        }
                    }
                    return isEvent ? $t("Event status changed") : $t('Project status changed');
                case "task_created":
                    return formatTaskAction($t('Task created'), item);
                case "task_changed":
                    return formatTaskAction($t('Task changed'), item);
                case "task_finished":
                    return formatTaskAction($t('Task finished'), item);
                case "task_not_finished":
                    return formatTaskAction($t('Task not finished'), item);
                case "task_deleted":
                    return formatTaskAction($t('Task deleted'), item);
                default:
                    return item.type;
            }
        }
        const formatDataValue = (val, t) => {
            if (val === null || val === "")
                return "";

            switch (t) {
                case "date":
                    return formatDate(val);
                case "user_single": {
                    let user = users.value.find(x => x.id === parseInt(val));
                    return user == null ? val : user.name;
                }
                case "user_multi": {
                    let val_arr = val.split(';');
                    return val_arr.map(x => {
                        let user = users.value.find(y => y.id === parseInt(x));
                        return user == null ? x : user.name;
                    }).join(", ");
                }
                case "supplier": {
                    let supplier = suppliers.value.find(x => x.id === parseInt(val));
                    return supplier == null ? val : supplier.name;
                }
                case "topic": {
                    let topic = topics.value.find(x => x.id === parseInt(val));
                    return topic == null ? val : topic.name;
                }
                case "client": {
                    let client = clients.value.find(x => x.id === parseInt(val));
                    return client == null ? val : client.name;
                }
                case "boolean":
                    return [1, "1", true, "true"].indexOf(val) >= 0 ? $t("Yes") : $t("No");
                case "number":
                case "integer":
                    return formatNumber(val);
                case "text":
                case "note":
                case "choice_single":
                case "choice_multi":
                case "task_status":
                case "project_status":
                    return val;
                default:
                    return val + " (" + t + ")";
            }
        }

        loadData();

        return {
            headers,
            items,
            anyNotCollapsed,
            toggleExpandAll,
            toggleExpand,
            formatAction,
            formatDataValue
        };
    }
}
</script>
